import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { Camera, User } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { login, signup, clearError } from '../../store/auth/authSlice';
import { UserRole, Zone, ZoneLabels } from '../../types';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signupSchema = z
  .object({
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
    campus: z.string().optional(),
    zone: z.nativeEnum(Zone).optional(),
    phoneNumber: z.string().max(20, 'Phone number must not exceed 20 characters').optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;

export const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, isAuthenticated, user } = useAppSelector(
    (state) => state.auth
  );

  // Set default tab based on URL path
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(
    location.pathname === '/signup' ? 'signup' : 'login'
  );
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  useEffect(() => {
    if (isAuthenticated && user) {
      const from = (location.state as any)?.from?.pathname ||
        (user.role === UserRole.ADMIN ? '/admin' : '/books');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch, activeTab]);

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onLoginSubmit = (data: LoginFormData) => {
    dispatch(login(data));
  };

  const onSignupSubmit = (data: SignupFormData) => {
    const { confirmPassword, ...signupData } = data;
    dispatch(signup({
      ...signupData,
      profileImage: profileImage || undefined,
      zone: signupData.zone || undefined,
      phoneNumber: signupData.phoneNumber || undefined,
    }));
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-no-repeat"
        style={{
          backgroundImage: 'url(/background2.jpeg)',
          backgroundPosition: 'center 100%',
          backgroundSize: 'cover',
        }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header with Logo */}
        <div className="flex flex-col items-center pt-12 pb-6">
          <div className="w-20 h-20 rounded-full border-2 border-primary-600/30 flex items-center justify-center mb-4 backdrop-blur-sm bg-white/10">
            <img src="/icon.png" alt="GHAFES Logo" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-primary-400 tracking-wider">
            GHAFES
          </h1>
          <p className="text-sm text-primary-500 tracking-wide">Library App</p>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex flex-col">
          {/* Tab Navigation */}
          <div className="flex justify-center gap-12 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`text-lg font-semibold pb-2 transition-all ${
                activeTab === 'login'
                  ? 'text-slate-900 border-b-2 border-primary-600'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setActiveTab('signup')}
              className={`text-lg font-semibold pb-2 transition-all ${
                activeTab === 'signup'
                  ? 'text-slate-900 border-b-2 border-primary-600'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Signup
            </button>
          </div>

          {/* Form Card */}
          <div className="flex-1 bg-white rounded-t-[2.5rem] px-6 pt-8 pb-10 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
            <div className="max-w-sm mx-auto">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Login Form */}
              {activeTab === 'login' && (
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      E-mail Address
                    </label>
                    <input
                      type="email"
                      placeholder="youremail@example.com"
                      {...loginForm.register('email')}
                      className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••••"
                      {...loginForm.register('password')}
                      className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                    />
                    {loginForm.formState.errors.password && (
                      <p className="text-red-500 text-xs mt-1">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    className="text-primary-700 font-semibold text-sm hover:text-primary-800 transition-colors"
                  >
                    Forgot password?
                  </button>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 bg-primary-700 text-white font-semibold rounded-xl hover:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-lg shadow-primary-700/30 mt-6"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      'LOGIN'
                    )}
                  </button>
                </form>
              )}

              {/* Signup Form */}
              {activeTab === 'signup' && (
                <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
                  {/* Profile Picture Upload */}
                  <div className="flex justify-center mb-4">
                    <label className="cursor-pointer group">
                      <div className="relative w-20 h-20">
                        <div className="w-full h-full rounded-full bg-primary-100 border-2 border-dashed border-primary-300 flex items-center justify-center overflow-hidden group-hover:border-primary-500 transition-colors">
                          {profilePreview ? (
                            <img src={profilePreview} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <User size={28} className="text-primary-400" />
                          )}
                        </div>
                        <div className="absolute bottom-0 right-0 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center shadow-lg group-hover:bg-primary-700 transition-colors">
                          <Camera size={12} className="text-white" />
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <p className="text-center text-xs text-slate-500 mb-2">Profile picture (optional)</p>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        First Name
                      </label>
                      <input
                        type="text"
                        placeholder="John"
                        {...signupForm.register('firstName')}
                        className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                      />
                      {signupForm.formState.errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">
                          {signupForm.formState.errors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Last Name
                      </label>
                      <input
                        type="text"
                        placeholder="Doe"
                        {...signupForm.register('lastName')}
                        className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                      />
                      {signupForm.formState.errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">
                          {signupForm.formState.errors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      E-mail Address
                    </label>
                    <input
                      type="email"
                      placeholder="youremail@example.com"
                      {...signupForm.register('email')}
                      className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                    />
                    {signupForm.formState.errors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {signupForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      placeholder="e.g. +233 XX XXX XXXX"
                      {...signupForm.register('phoneNumber')}
                      className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Campus
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Legon"
                        {...signupForm.register('campus')}
                        className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Zone
                      </label>
                      <select
                        {...signupForm.register('zone')}
                        className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 transition-colors"
                      >
                        <option value="">Select Zone</option>
                        {Object.values(Zone).map((zone) => (
                          <option key={zone} value={zone}>
                            {ZoneLabels[zone]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••••"
                      {...signupForm.register('password')}
                      className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                    />
                    {signupForm.formState.errors.password && (
                      <p className="text-red-500 text-xs mt-1">
                        {signupForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••••"
                      {...signupForm.register('confirmPassword')}
                      className="w-full px-0 py-2 bg-transparent border-b border-slate-300 focus:border-primary-600 focus:outline-none text-slate-800 placeholder:text-slate-400 transition-colors"
                    />
                    {signupForm.formState.errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">
                        {signupForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 bg-primary-700 text-white font-semibold rounded-xl hover:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-lg shadow-primary-700/30 mt-4"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Creating account...
                      </span>
                    ) : (
                      'CREATE ACCOUNT'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
